/**
 * The batch bar (founder ruling 2026-08-25, extended by the batch train
 * 2026-08-31: "we don't yet have batch processing, including batch
 * compiling. make this happen").
 *
 * The real motion at month end is "move everything that is ready", not
 * one click per invoice. The bar is STATUS-AWARE: it looks at what is
 * selected and offers the applicable act for each subset - approve for
 * the selected drafts, issue for the selected approved, and the original
 * push for the selected issued-and-unpushed. A mixed selection offers
 * each act scoped to ITS OWN subset, labeled with its count; running one
 * act never touches another act's rows.
 *
 * THE STRUCTURAL LAW: a batch is a LOOP over the same per-invoice path
 * (pushInvoicesSequentially / approveInvoicesSequentially /
 * issueInvoicesSequentially). No bulk endpoint, no shared transaction.
 * Each invoice keeps its own attempt row, so twenty invoices with three
 * problems is seventeen done and three named outcomes, and a re-run
 * cannot duplicate the seventeen (approve/issue on a moved row answers
 * 409 illegal_transition, which the report files as the benign
 * "changed status", never a failure).
 *
 * The push report keeps its ruled buckets, because "in QuickBooks but
 * the total is not certified" is neither a success nor a retryable
 * failure (sweep F6). The approve/issue reports speak the same report
 * language: one summary sentence of per-bucket clauses, then a named
 * list per non-empty bucket.
 */

import { type ReactNode, useState } from 'react';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import { canApproveInvoice, canIssueInvoice, canPushInvoice } from '@/lib/capabilities';
import {
  pushInvoicesSequentially,
  qboReasonSentence,
  type BatchPushOutcome,
  type BatchPushReport,
  type BatchPushTarget,
} from '@/lib/qboApi';
import {
  approveInvoicesSequentially,
  issueInvoicesSequentially,
  batchActionReasonSentence,
  batchStopSentence,
  type BatchActionReport,
  type BatchActionTarget,
} from '@/lib/billingBatch';
import { Button } from '@/components/primitives/Button';

export interface BatchPushBarProps {
  /** Selected issued-and-unpushed rows: the push act's payload. */
  selected: readonly BatchPushTarget[];
  /** Selected draft rows: the approve act's payload. */
  drafts?: readonly BatchActionTarget[];
  /** Selected approved rows: the issue act's payload. */
  approved?: readonly BatchActionTarget[];
  /** Fired once a batch settles, so the list can refresh. */
  onDone: () => void;
}

function label(target: { doc_number: string | null; client_display_name: string }): string {
  const doc = target.doc_number ?? '';
  const name = target.client_display_name;
  if (doc !== '' && name !== '') return `${name} (${doc})`;
  return doc !== '' ? doc : name;
}

function FailureList({
  testId,
  heading,
  rows,
}: {
  testId: string;
  heading: string;
  rows: BatchPushOutcome[];
}): ReactNode {
  const contact = useSupportContact();
  return (
    <div data-testid={testId} className="mt-2 space-y-1">
      <h4 className="text-xs font-semibold text-sage-800">{heading}</h4>
      <ul className="space-y-1">
        {rows.map(({ target, result }) => (
          <li
            key={target.id}
            data-testid={`qbo-batch-failure-${target.id}`}
            className="text-xs text-ink-soft"
          >
            <span className="font-medium text-ink">{label(target)}</span>{' '}
            {qboReasonSentence(result.reason, contact)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "1 invoice" / "{n} invoices". */
function invoices(n: number): string {
  return n === 1 ? '1 invoice' : `${n} invoices`;
}

/** One completed run, whatever the act: the bar holds the LATEST report
 *  and renders it in the one shared report language. */
type BarReport =
  | { kind: 'push'; report: BatchPushReport }
  | { kind: 'approve' | 'issue'; report: BatchActionReport };

/** The push report: its buckets and their register are ruled ("did not
 *  send" for the failed bucket alone, "unconfirmed" where no answer came
 *  back) and pinned. Every count names "invoice" or "invoices". */
function PushReportView({ report }: { report: BatchPushReport }): ReactNode {
  return (
    <div className="space-y-1">
      {/* One clause per non-empty bucket. "did not send" belongs to
          the failed bucket ALONE: the unconfirmed one left Olive and
          no answer came back, so the honest word is "unconfirmed",
          in the same register as the ruled chip label. */}
      <p data-testid="qbo-batch-summary" className="text-sm text-ink">
        {[
          report.done.length > 0 ? `${invoices(report.done.length)} sent to QuickBooks.` : null,
          report.already.length > 0 ? `${invoices(report.already.length)} already in QuickBooks.` : null,
          report.attention.length > 0
            ? `${invoices(report.attention.length)} ${report.attention.length === 1 ? 'needs' : 'need'} a look.`
            : null,
          report.unconfirmed.length > 0 ? `${invoices(report.unconfirmed.length)} unconfirmed.` : null,
          report.failed.length > 0 ? `${invoices(report.failed.length)} did not send.` : null,
        ]
          .filter((clause) => clause !== null)
          .join(' ')}
      </p>

      {report.already.length > 0 && (
        <div data-testid="qbo-batch-already" className="mt-2 space-y-1">
          <h4 className="text-xs font-semibold text-sage-800">
            {`${invoices(report.already.length)} already in QuickBooks`}
          </h4>
          <p className="text-xs text-ink-soft">
            These invoices were already in QuickBooks, so nothing was sent. There's nothing more to do.
          </p>
          <ul className="space-y-1">
            {report.already.map(({ target, result }) => (
              <li
                key={target.id}
                data-testid={`qbo-batch-already-${target.id}`}
                className="text-xs text-ink-soft"
              >
                <span className="font-medium text-ink">{label(target)}</span>
                {result.qboInvoiceId !== undefined
                  ? ` (QuickBooks invoice ${result.qboInvoiceId})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.attention.length > 0 && (
        <div data-testid="qbo-batch-attention" className="mt-2 space-y-1">
          <h4 className="text-xs font-semibold text-sage-800">
            {`${invoices(report.attention.length)} in QuickBooks, total not confirmed`}
          </h4>
          <p className="text-xs text-ink-soft">
            These invoices reached QuickBooks, but their totals there couldn't be confirmed.
            Open each one in QuickBooks and compare the total, and don't send them again.
          </p>
          <ul className="space-y-1">
            {report.attention.map(({ target, result }) => (
              <li
                key={target.id}
                data-testid={`qbo-batch-attention-${target.id}`}
                className="text-xs text-ink-soft"
              >
                <span className="font-medium text-ink">{label(target)}</span>
                {result.qboInvoiceId !== undefined
                  ? ` (QuickBooks invoice ${result.qboInvoiceId})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.unconfirmed.length > 0 && (
        <FailureList
          testId="qbo-batch-unconfirmed"
          heading={`${invoices(report.unconfirmed.length)} unconfirmed`}
          rows={report.unconfirmed}
        />
      )}

      {report.failed.length > 0 && (
        <FailureList
          testId="qbo-batch-failures"
          heading={`${invoices(report.failed.length)} did not send`}
          rows={report.failed}
        />
      )}
    </div>
  );
}

/** The approve/issue report: the same report language as the push
 *  (summary clauses, then a named list per non-empty bucket). */
function ActionReportView({
  kind,
  report,
}: {
  kind: 'approve' | 'issue';
  report: BatchActionReport;
}): ReactNode {
  const contact = useSupportContact();
  const doneWord = kind === 'approve' ? 'approved' : 'issued';
  return (
    <div className="space-y-1">
      <p data-testid="invoice-batch-summary" className="text-sm text-ink">
        {[
          report.done.length > 0 ? `${invoices(report.done.length)} ${doneWord}.` : null,
          report.state_changed.length > 0
            ? `${invoices(report.state_changed.length)} had already changed status, so this run left ${
                report.state_changed.length === 1 ? 'that invoice' : 'those invoices'
              } alone.`
            : null,
          report.failed.length > 0 ? `${invoices(report.failed.length)} refused.` : null,
          report.stopped !== null ? 'Stopped early.' : null,
        ]
          .filter((clause) => clause !== null)
          .join(' ')}
      </p>

      {report.state_changed.length > 0 && (
        <div data-testid="invoice-batch-state-changed" className="mt-2 space-y-1">
          <h4 className="text-xs font-semibold text-sage-800">
            {`${invoices(report.state_changed.length)} changed status`}
          </h4>
          <p className="text-xs text-ink-soft">
            These invoices had already moved on, so nothing changed. There's nothing more
            to do.
          </p>
          <ul className="space-y-1">
            {report.state_changed.map(({ target }) => (
              <li
                key={target.id}
                data-testid={`invoice-batch-state-changed-${target.id}`}
                className="text-xs text-ink-soft"
              >
                <span className="font-medium text-ink">{label(target)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.failed.length > 0 && (
        <div data-testid="invoice-batch-failed" className="mt-2 space-y-1">
          <h4 className="text-xs font-semibold text-sage-800">
            {`${invoices(report.failed.length)} refused`}
          </h4>
          <ul className="space-y-1">
            {report.failed.map(({ target, reason }) => (
              <li
                key={target.id}
                data-testid={`invoice-batch-failed-${target.id}`}
                className="text-xs text-ink-soft"
              >
                <span className="font-medium text-ink">{label(target)}</span>{' '}
                {batchActionReasonSentence(reason, contact)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.stopped !== null && (
        <p data-testid="invoice-batch-stopped" className="mt-2 text-xs text-pepper-deep">
          {batchStopSentence(report.stopped, kind)}
        </p>
      )}
    </div>
  );
}

export function BatchPushBar({
  selected,
  drafts = [],
  approved = [],
  onDone,
}: BatchPushBarProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  // Each act asks ITS OWN question (founder ruling 2026-08-26 split
  // push_invoice off manage_billing_integration; the same discipline
  // holds for approve_invoice and issue_invoice). Asking the wrong one
  // hides an act from the person who actually runs month end.
  const canApprove = canApproveInvoice(whoami);
  const canIssue = canIssueInvoice(whoami);
  const canPush = canPushInvoice(whoami);
  const [running, setRunning] = useState<'approve' | 'issue' | 'push' | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [report, setReport] = useState<BarReport | null>(null);

  const offerApprove = canApprove && drafts.length > 0;
  const offerIssue = canIssue && approved.length > 0;
  const offerPush = canPush && selected.length > 0;

  if (!canApprove && !canIssue && !canPush) return null;
  // THE BAR SURVIVES WHILE IT HOLDS A REPORT (founder regression,
  // 2026-08-26: "it just reverts and goes nowhere"). The payload gates
  // empty the subsets on the refetch after a run, and for a one-invoice
  // batch that is EVERY successful run: unmounting here would take the
  // report with it, and the report is the only place the operator learns
  // what happened. An empty selection with no report is genuinely
  // nothing, and renders as nothing.
  if (!offerApprove && !offerIssue && !offerPush && report === null) return null;

  const runPush = async (): Promise<void> => {
    setRunning('push');
    setReport(null);
    setProgress({ done: 0, total: selected.length });
    const result = await pushInvoicesSequentially(tenantSlug, selected, {
      onProgress: (done, total) => setProgress({ done, total }),
    });
    setReport({ kind: 'push', report: result });
    setRunning(null);
    onDone();
  };

  const runAction = async (kind: 'approve' | 'issue'): Promise<void> => {
    const targets = kind === 'approve' ? drafts : approved;
    const drive = kind === 'approve' ? approveInvoicesSequentially : issueInvoicesSequentially;
    setRunning(kind);
    setReport(null);
    setProgress({ done: 0, total: targets.length });
    const result = await drive(tenantSlug, targets, {
      onProgress: (done, total) => setProgress({ done, total }),
    });
    setReport({ kind, report: result });
    setRunning(null);
    onDone();
  };

  return (
    <div
      data-testid="qbo-batch-bar"
      className="border border-sage-200 bg-sage-50 rounded-card p-3 sm:p-4 space-y-2"
    >
      {/* Nothing left to act on is nothing to offer: the one thing this
          control may never do is offer to re-run the invoice that just
          moved. The report below outlives the selection that made it. */}
      {(offerApprove || offerIssue || offerPush) && (
        <div className="flex flex-wrap items-center gap-3">
          {offerApprove && (
            <Button
              data-testid="invoice-batch-approve"
              onClick={() => void runAction('approve')}
              loading={running === 'approve'}
              disabled={running !== null}
            >
              {`Approve ${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'}`}
            </Button>
          )}
          {offerIssue && (
            <Button
              data-testid="invoice-batch-issue"
              onClick={() => void runAction('issue')}
              loading={running === 'issue'}
              disabled={running !== null}
            >
              {`Issue ${approved.length} approved ${approved.length === 1 ? 'invoice' : 'invoices'}`}
            </Button>
          )}
          {offerPush && (
            <Button
              data-testid="qbo-batch-push"
              onClick={() => void runPush()}
              loading={running === 'push'}
              disabled={running !== null}
            >
              {`Send ${invoices(selected.length)} to QuickBooks`}
            </Button>
          )}
          {running !== null && progress && (
            <span data-testid="qbo-batch-progress" className="text-xs text-ink-soft">
              {`${progress.done} of ${invoices(progress.total)}`}
            </span>
          )}
        </div>
      )}

      {report !== null &&
        (report.kind === 'push' ? (
          <PushReportView report={report.report} />
        ) : (
          <ActionReportView kind={report.kind} report={report.report} />
        ))}
    </div>
  );
}
